import { useState } from 'react';

import { AppIcon, PriorityPlaylist } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { useGetPlaylistsQuery } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  closePlaylistDrawer,
  openNewPlaylistDialog,
  openNewTaskDialog,
  openPlaylistViewStep,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { Box, SxProps, Theme, Typography, TextField, Button } from '@mui/material';

import { PlaylistCard } from './PlaylistCard';
import { PLAYLIST_CARD_DRAWER_WIDTH } from '../constants/priorities.constants';

const PLAYLIST_DRAWER_STRINGS = appStrings.modules.priorities.playlistDrawer;

type PlaylistListDrawerProps = Readonly<{
  handleClose: () => void;
}>;

export const PlaylistListDrawer = ({ handleClose }: PlaylistListDrawerProps) => {
  const dispatch = useAppDispatch();

  const { data: playlists, isLoading: isLoadingPlaylists } = useGetPlaylistsQuery(undefined);

  const [playlistSearchTerm, setPlaylistSearchTerm] = useState('');

  // if there is a search term, filter the playlists by title and description
  const filteredPlaylists = playlists?.filter((playlist) =>
    playlistSearchTerm
      ? playlist.title.toLowerCase().includes(playlistSearchTerm.toLowerCase()) ||
        playlist.description?.toLowerCase().includes(playlistSearchTerm.toLowerCase())
      : playlists,
  );

  const handlePlaylistSelection = (playlist: PriorityPlaylist) => {
    dispatch(openPlaylistViewStep(playlist));
    dispatch(openNewTaskDialog({ playlist, shouldAutoOpenPlaylistDrawer: true }));
  };

  if (isLoadingPlaylists) {
    return null;
  }

  return (
    <>
      <Box sx={headerSx}>
        <Box component="img" src="/images/playlist-list-drawer.svg" />

        <Typography variant="text-xl-semibold">{PLAYLIST_DRAWER_STRINGS.title}</Typography>

        <Box onClick={handleClose} sx={closeButtonSx}>
          <AppIcon iconName="XClose" diameter="20px" color="Colors/Foreground/fg-quaternary" />
        </Box>
      </Box>

      <TextField
        id="playlist-search-input"
        variant="outlined"
        placeholder={PLAYLIST_DRAWER_STRINGS.searchBarPlaceholder}
        value={playlistSearchTerm}
        onChange={(e) => setPlaylistSearchTerm(e.target.value)}
        fullWidth
        sx={searchBarSx}
        InputProps={{
          startAdornment: (
            <AppIcon
              iconName="SearchMd"
              diameter="20px"
              color="Colors/Background/bg-primary-solid"
            />
          ),
        }}
      />

      <Box sx={playlistsContainerSx}>
        {filteredPlaylists?.map((playlist) => (
          <PlaylistCard
            key={playlist.id}
            id={`playlist-picker-card-${playlist.id}`}
            playlist={playlist}
            onClick={() => handlePlaylistSelection(playlist)}
          />
        ))}
      </Box>

      <Box sx={buttonGroupSx}>
        <Button
          id="playlist-create-button"
          hierarchy="Primary"
          size="lg"
          fullWidth
          onClick={() => {
            dispatch(openNewPlaylistDialog());
          }}
        >
          {PLAYLIST_DRAWER_STRINGS.createPlaylist}
        </Button>
        <Button
          id="playlist-dont-add-button"
          hierarchy="Secondary gray"
          size="lg"
          fullWidth
          onClick={() => {
            dispatch(closePlaylistDrawer());
            dispatch(openNewTaskDialog({ playlist: null, shouldAutoOpenPlaylistDrawer: false }));
          }}
        >
          {PLAYLIST_DRAWER_STRINGS.dontAdd}
        </Button>
      </Box>
    </>
  );
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  gap: backrs.spacing['spacing-md'],
  marginBottom: backrs.spacing['spacing-3xl'],
  px: backrs.spacing['spacing-sm'],
});

const closeButtonSx: SxProps<Theme> = {
  cursor: 'pointer',
  marginLeft: 'auto',
};

const playlistsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-xs'],
  minWidth: {
    xs: `calc(100vw - (${backrs.spacing['spacing-4xl']}))`,
    sm: `calc(${PLAYLIST_CARD_DRAWER_WIDTH}px + (${backrs.spacing['spacing-sm']} * 2))`,
  },
  px: backrs.spacing['spacing-sm'],
  pb: backrs.spacing['spacing-xl'],
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
});

const searchBarSx: SxProps<Theme> = ({ backrs }) => ({
  // to handle the hover animation of the playlist cards, we need to subtract the padding of the playlists container
  marginBottom: `calc(${backrs.spacing['spacing-5xl']} - ${backrs.spacing['spacing-xs']}) !important`,
  px: backrs.spacing['spacing-sm'],
});

const buttonGroupSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-xl'],
  paddingBottom: backrs.spacing['spacing-3xl'],
  backgroundColor: backrs.colors['Colors/Base/White'],

  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});
